import { API_CONFIG } from './config';
import { withRequestTimeout } from './requestTimeout';
import { refreshAccessTokenWithStatus, shouldSkipAuthRefresh } from '../authRefreshCoordinator';
import { buildAuthHeaders } from '../authTokenStore';

function parseResponseBody(rawBody, contentType = '', { strictJsonContentType = false } = {}) {
    if (typeof rawBody !== 'string' || rawBody.length === 0) {
        return null;
    }

    const normalizedContentType = String(contentType || '').split(';')[0].trim().toLowerCase();
    const shouldParseJson = normalizedContentType.includes('json');

    if (shouldParseJson) {
        try {
            return JSON.parse(rawBody);
        } catch (parseError) {
            if (strictJsonContentType) {
                throw parseError;
            }
        }
    }

    try {
        // Attempt JSON parsing even if content-type is missing but body contains JSON.
        return JSON.parse(rawBody);
    } catch (_) {
        return rawBody;
    }
}

const appendQueryParams = (endpoint, params) => {
    if (!params || typeof params !== 'object') {
        return endpoint;
    }

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined) {
            return;
        }
        if (Array.isArray(value)) {
            value.forEach((entry) => {
                if (entry !== null && entry !== undefined) {
                    searchParams.append(key, String(entry));
                }
            });
            return;
        }
        searchParams.append(key, String(value));
    });

    const queryString = searchParams.toString();
    if (!queryString) {
        return endpoint;
    }

    return `${endpoint}${String(endpoint).includes('?') ? '&' : '?'}${queryString}`;
};

const isFormDataBody = (body) => (
    typeof FormData !== 'undefined' && body instanceof FormData
);

const isRawRequestBody = (body) => (
    isFormDataBody(body)
    || (typeof Blob !== 'undefined' && body instanceof Blob)
    || (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer)
    || (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams)
    || typeof body === 'string'
);

const resolveRequestHeaders = (defaultHeaders, headers, { omitContentType = false } = {}) => {
    const resolved = {
        ...defaultHeaders,
        ...headers,
    };

    Object.keys(resolved).forEach((key) => {
        if (resolved[key] === undefined || resolved[key] === null) {
            delete resolved[key];
        }
    });

    if (omitContentType) {
        Object.keys(resolved).forEach((key) => {
            if (key.toLowerCase() === 'content-type') {
                delete resolved[key];
            }
        });
    }

    return resolved;
};

const resolveErrorStatus = (error) => {
    if (typeof error?.status === 'number') {
        return error.status;
    }
    if (typeof error?.response?.status === 'number') {
        return error.response.status;
    }
    return null;
};

const shouldSuppressErrorLog = (error, suppressErrorStatusLogs) => {
    const status = resolveErrorStatus(error);
    if (status === null) {
        return false;
    }
    if (suppressErrorStatusLogs instanceof Set) {
        return suppressErrorStatusLogs.has(status);
    }
    if (Array.isArray(suppressErrorStatusLogs)) {
        return suppressErrorStatusLogs.includes(status);
    }
    return false;
};

class HttpService {
    constructor() {
        this.baseUrl = API_CONFIG.BASE_URL;
        this.defaultHeaders = API_CONFIG.DEFAULT_HEADERS;
    }

    async request(endpoint, options = {}) {
        return this.requestWithRetry(endpoint, options, true);
    }

    async requestWithRetry(endpoint, options, allowRefresh) {
        const {
            method = 'GET',
            headers = {},
            body,
            credentials = 'include',
            skipAuthRefresh = false,
            skipAuthHeader = false,
            signal,
            timeoutMs = API_CONFIG.REQUEST_TIMEOUT_MS,
            params,
            responseType,
            suppressErrorStatusLogs,
        } = options;
        const requestEndpoint = appendQueryParams(endpoint, params);
        const timeout = withRequestTimeout({
            signal,
            timeoutMs,
            method,
            url: requestEndpoint,
        });
        const hasRequestBody = body !== undefined && body !== null;
        const omitContentType = hasRequestBody && isFormDataBody(body);
        const authHeaders = credentials === 'omit' || skipAuthHeader ? {} : buildAuthHeaders();

        const requestOptions = {
            method,
            headers: resolveRequestHeaders(this.defaultHeaders, {
                ...authHeaders,
                ...headers,
            }, { omitContentType }),
            credentials,
            signal: timeout.signal,
        };

        if (hasRequestBody) {
            requestOptions.body = isRawRequestBody(body) ? body : JSON.stringify(body);
        }

        try {
            const response = await fetch(requestEndpoint, requestOptions);

            if (!response.ok) {
                if (response.status === 401 && allowRefresh && !skipAuthRefresh && !shouldSkipAuthRefresh(requestEndpoint)) {
                    const refreshResult = await this.refreshAccessToken(requestEndpoint);
                    if (refreshResult.ok) {
                        return this.requestWithRetry(endpoint, options, false);
                    }
                }
                const error = new Error(`HTTP error! status: ${response.status}`);
                error.status = response.status;
                error.statusText = response.statusText;
                try {
                    const errorBody = await response.text();
                    if (errorBody) {
                        error.body = parseResponseBody(
                            errorBody,
                            response.headers.get('content-type') || '',
                            { strictJsonContentType: false },
                        );
                    }
                } catch (_) {
                    // no-op: best effort to include response text
                }
                throw error;
            }

            const status = response.status;
            const contentLength = response.headers.get('content-length');
            const hasBody = status !== 204
                && status !== 205
                && contentLength !== '0';

            if (!hasBody) {
                return null;
            }

            if (responseType === 'blob') {
                return await response.blob();
            }

            const rawBody = await response.text();
            if (!rawBody) {
                return null;
            }
            return parseResponseBody(
                rawBody,
                response.headers.get('content-type') || '',
                { strictJsonContentType: true },
            );
        } catch (error) {
            const timeoutError = timeout.getTimeoutError();
            if (timeoutError) {
                if (!shouldSuppressErrorLog(timeoutError, suppressErrorStatusLogs)) {
                    console.error('API request failed:', timeoutError);
                }
                throw timeoutError;
            }
            if (!shouldSuppressErrorLog(error, suppressErrorStatusLogs)) {
                console.error('API request failed:', error);
            }
            throw error;
        } finally {
            timeout.cleanup();
        }
    }

    async refreshAccessToken(endpoint) {
        return refreshAccessTokenWithStatus({
            trigger: `fetch-401:${endpoint || 'unknown'}`,
        });
    }

    get(endpoint, options = {}) {
        return this.request(endpoint, { ...options, method: 'GET' });
    }

    post(endpoint, body, options = {}) {
        return this.request(endpoint, { ...options, method: 'POST', body });
    }

    put(endpoint, body, options = {}) {
        return this.request(endpoint, { ...options, method: 'PUT', body });
    }

    patch(endpoint, body, options = {}) {
        return this.request(endpoint, { ...options, method: 'PATCH', body });
    }

    delete(endpoint, options = {}) {
        return this.request(endpoint, { ...options, method: 'DELETE' });
    }
}

export const httpService = new HttpService(); 
